import React from 'react';
import PropTypes from 'prop-types';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Table,
	TableHeader,
	TableBody,
	TableColumn,
	TableRow,
	TableCell,
	Select,
	SelectItem,
	Autocomplete,
	AutocompleteItem,
	Button,
	Spinner,
	Chip,
	addToast
} from '@heroui/react';
import { Users, Trash2, Plus } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';

// Fallback accessibility options (used if API doesn't return them)
const DEFAULT_ACCESS_STATUSES = [
	{ value: 'see', name: 'can see (including reports)' },
	{ value: 'reserve', name: 'can reserve (including reports)' },
	{ value: 'noAccess', name: 'no access (including reports)' },
	{ value: 'reserveInstructorOnly', name: 'can reserve with instructor only (including reports)' }
];

function ResourcePermissionsModal({
	isOpen,
	onClose,
	resource = null,
	users: usersProp = []
}) {
	// State
	const [loading, setLoading] = React.useState(false);
	const [saving, setSaving] = React.useState(false);
	const [resourceAccessibility, setResourceAccessibility] = React.useState('');
	const [userPermissions, setUserPermissions] = React.useState([]);
	const [organizationUsers, setOrganizationUsers] = React.useState([]);
	const [accessibilityTypes, setAccessibilityTypes] = React.useState(DEFAULT_ACCESS_STATUSES);

	// Add user form state
	const [selectedUserId, setSelectedUserId] = React.useState('');
	const [selectedUserAccessibility, setSelectedUserAccessibility] = React.useState('');
	const [addingUser, setAddingUser] = React.useState(false);

	// Fetch access data and users when modal opens
	React.useEffect(() => {
		if (isOpen && resource?.id) {
			fetchAccessData();
			fetchOrganizationUsers();
		}
	}, [isOpen, resource?.id]);

	// Reset state when modal closes
	React.useEffect(() => {
		if (!isOpen) {
			setResourceAccessibility('');
			setUserPermissions([]);
			setOrganizationUsers([]);
			setSelectedUserId('');
			setSelectedUserAccessibility('');
			setAccessibilityTypes(DEFAULT_ACCESS_STATUSES);
		}
	}, [isOpen]);

	// Fetch organization users from API
	const fetchOrganizationUsers = async () => {
		try {
			const response = await ResourcesApi.getOrganizationUsersList();
			const data = response.data?.data || response.data || response;
			const users = Array.isArray(data) ? data : data.users || [];
			setOrganizationUsers(users);
		} catch (err) {
			console.error('Failed to fetch organization users:', err);
			addToast({
				title: 'Error',
				description: 'Failed to load organization users',
				color: 'danger'
			});
		}
	};

	// Fetch access settings from API
	const fetchAccessData = async () => {
		setLoading(true);
		try {
			const response = await ResourcesApi.getAccess(resource.id);
			// API returns { data: { success, data: { userSettings, availableAccessibilityTypes } } }
			const responseData = response.data || response;
			const data = responseData.data || responseData;

			// Resource accessibility comes from resource object or direct field
			const accessibility = data.accessibility
				|| data.resource?.accessibility
				|| resource?.accessibility
				|| 'reserve';
			setResourceAccessibility(accessibility);

			// User permissions - API returns userSettings array with userId, name, surname, accessibility
			const users = data.userSettings || data.users || data.userPermissions || [];
			// Handle if response is directly the array
			const usersList = Array.isArray(users) ? users : [];
			setUserPermissions(usersList);

			// Accessibility types from API
			if (data.availableAccessibilityTypes && Array.isArray(data.availableAccessibilityTypes)) {
				setAccessibilityTypes(data.availableAccessibilityTypes);
			}
		} catch (err) {
			console.error('Access API error:', err);
			const errorMsg = err.response?.data?.message
				|| err.response?.data?.error
				|| err.message
				|| 'Failed to load access settings';
			addToast({
				title: 'Error',
				description: errorMsg,
				color: 'danger'
			});
		} finally {
			setLoading(false);
		}
	};

	// Handle resource accessibility change
	const handleResourceAccessibilityChange = async keys => {
		const newAccessibility = Array.from(keys)[0];
		if (!newAccessibility || newAccessibility === resourceAccessibility) return;

		const previousValue = resourceAccessibility;
		setResourceAccessibility(newAccessibility);
		setSaving(true);

		try {
			await ResourcesApi.updateAccess(resource.id, newAccessibility);
			addToast({
				title: 'Success',
				description: 'Resource accessibility updated',
				color: 'success'
			});
		} catch (err) {
			setResourceAccessibility(previousValue);
			addToast({
				title: 'Error',
				description: err.response?.data?.message || 'Failed to update accessibility',
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Handle user accessibility change
	const handleUserAccessibilityChange = async (userId, keys) => {
		const newAccessibility = Array.from(keys)[0];
		if (!newAccessibility) return;

		const user = userPermissions.find(u => String(u.userID || u.userId || u.id) === String(userId));
		if (!user) return;

		const previousValue = user.accessibility;

		// Optimistic update
		setUserPermissions(prev =>
			prev.map(u =>
				String(u.userID || u.userId || u.id) === String(userId)
					? { ...u, accessibility: newAccessibility }
					: u
			)
		);

		try {
			await ResourcesApi.updateUserAccess(resource.id, userId, newAccessibility);
			addToast({
				title: 'Success',
				description: 'User permission updated',
				color: 'success'
			});
		} catch (err) {
			setUserPermissions(prev =>
				prev.map(u =>
					String(u.userID || u.userId || u.id) === String(userId)
						? { ...u, accessibility: previousValue }
						: u
				)
			);
			addToast({
				title: 'Error',
				description: err.response?.data?.message || 'Failed to update user permission',
				color: 'danger'
			});
		}
	};

	// Handle add user permission
	const handleAddUser = async () => {
		if (!selectedUserId || !selectedUserAccessibility) {
			addToast({
				title: 'Warning',
				description: 'Please select a user and accessibility level',
				color: 'warning'
			});
			return;
		}

		setAddingUser(true);
		try {
			await ResourcesApi.addUserAccess(resource.id, selectedUserId, selectedUserAccessibility);

			// Add user to local state
			const selectedUser = availableUsers.find(
				u => String(u.userID || u.userId || u.id) === String(selectedUserId)
			);
			if (selectedUser) {
				setUserPermissions(prev => [
					...prev,
					{
						userID: selectedUserId,
						name: selectedUser.name,
						surname: selectedUser.surname,
						accessibility: selectedUserAccessibility
					}
				]);
			}

			// Reset form
			setSelectedUserId('');
			setSelectedUserAccessibility('');

			addToast({
				title: 'Success',
				description: 'User permission added',
				color: 'success'
			});
		} catch (err) {
			addToast({
				title: 'Error',
				description: err.response?.data?.message || 'Failed to add user permission',
				color: 'danger'
			});
		} finally {
			setAddingUser(false);
		}
	};

	// Handle delete user permission
	const handleDeleteUser = async userId => {
		const userToRemove = userPermissions.find(
			u => String(u.userID || u.userId || u.id) === String(userId)
		);

		// Optimistic delete
		setUserPermissions(prev =>
			prev.filter(u => String(u.userID || u.userId || u.id) !== String(userId))
		);

		try {
			await ResourcesApi.deleteUserAccess(resource.id, userId);
			addToast({
				title: 'Success',
				description: 'User permission removed',
				color: 'success'
			});
		} catch (err) {
			if (userToRemove) {
				setUserPermissions(prev => [...prev, userToRemove]);
			}
			addToast({
				title: 'Error',
				description: err.response?.data?.message || 'Failed to remove user permission',
				color: 'danger'
			});
		}
	};

	// Enrich user permissions with names from organization users
	const enrichedUserPermissions = React.useMemo(() => {
		if (!userPermissions.length) return [];

		// Create a map of organization users by ID for quick lookup
		const userMap = new Map();
		organizationUsers.forEach(u => {
			const id = String(u.userID || u.userId || u.id);
			userMap.set(id, {
				...u,
				middlename: u.middlename || u.middleName
			});
		});

		// Enrich each permission with user data
		return userPermissions.map(perm => {
			const permUserId = String(perm.userID || perm.userId || perm.id);
			const orgUser = userMap.get(permUserId);

			if (orgUser) {
				return {
					...perm,
					name: perm.name || orgUser.name,
					surname: perm.surname || orgUser.surname,
					middlename: perm.middlename || perm.middleName || orgUser.middlename || orgUser.middleName,
					email: perm.email || orgUser.email,
					roles: perm.roles || orgUser.roles || []
				};
			}
			return perm;
		});
	}, [userPermissions, organizationUsers]);

	// Check if user has Inactive role
	const isUserInactive = user => {
		const roles = user.roles || [];
		return roles.some(role =>
			role.name?.toLowerCase() === 'inactive' || role.id === 6
		);
	};

	// Filter users that don't already have permissions and are not inactive, sort alphabetically
	const availableUsers = React.useMemo(() => {
		const existingUserIds = new Set(
			userPermissions.map(u => String(u.userID || u.userId || u.id))
		);
		return organizationUsers
			.filter(u =>
				!existingUserIds.has(String(u.userID || u.userId || u.id)) &&
				!isUserInactive(u)
			)
			.sort((a, b) => {
				const nameA = `${a.name || ''} ${a.surname || ''}`.toLowerCase().trim();
				const nameB = `${b.name || ''} ${b.surname || ''}`.toLowerCase().trim();
				return nameA.localeCompare(nameB);
			});
	}, [organizationUsers, userPermissions]);

	// Get user display name
	const getUserDisplayName = user => {
		const name = user.name || '';
		const surname = user.surname || '';
		const middlename = user.middlename || user.middleName || '';

		if (name && surname) {
			return middlename ? `${name} ${middlename} ${surname}` : `${name} ${surname}`;
		}
		return name || user.email || `User #${user.userID || user.userId || user.id}`;
	};

	// Get accessibility display name (without parentheses part)
	const formatAccessibilityName = name => {
		if (!name) return '';
		// Remove text in parentheses, e.g., "can see (including reports)" -> "can see"
		return name.replace(/\s*\([^)]*\)\s*/g, '').trim();
	};

	// Get accessibility display name
	const getAccessibilityName = value => {
		const status = accessibilityTypes.find(s => s.value === value);
		return formatAccessibilityName(status?.name) || value;
	};

	// Get resource type name
	const getResourceTypeName = () => {
		return resource?.typeName || resource?.type?.name || 'Resource';
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
			<ModalContent>
				<ModalHeader className="flex items-center gap-2">
					<Users className="w-5 h-5" />
					Resource Permissions: {resource?.name}
				</ModalHeader>
				<ModalBody>
					{loading ? (
						<div className="flex justify-center py-8">
							<Spinner />
						</div>
					) : (
						<div className="space-y-6">
							{/* Add Personal Permission - above table */}
							<div className="bg-default-50 dark:bg-default-100/50 rounded-lg p-4">
								<h4 className="font-semibold mb-2">Add Personal Permission</h4>
								<p className="text-sm text-default-500 mb-2">
									You can give specific permission to users
								</p>
								<p className="text-xs text-default-400 mb-4">
									* All accessibility settings include reports access
								</p>

								<div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end">
									<Autocomplete
										label="User"
										placeholder="Select User"
										aria-label="Select user"
										selectedKey={selectedUserId || null}
										onSelectionChange={key => setSelectedUserId(key ? String(key) : '')}
										size="sm"
										className="w-full sm:flex-1"
										isDisabled={availableUsers.length === 0}
										defaultItems={availableUsers}
									>
										{user => (
											<AutocompleteItem key={String(user.userID || user.userId || user.id)}>
												{getUserDisplayName(user)}
											</AutocompleteItem>
										)}
									</Autocomplete>

									<Select
										label="Accessibility"
										placeholder="Select accessibility"
										aria-label="Select accessibility"
										selectedKeys={selectedUserAccessibility ? [selectedUserAccessibility] : []}
										onSelectionChange={keys =>
											setSelectedUserAccessibility(Array.from(keys)[0] || '')
										}
										size="sm"
										className="w-full sm:flex-1"
									>
										{accessibilityTypes.map(status => (
											<SelectItem key={status.value}>
												{formatAccessibilityName(status.name)}
											</SelectItem>
										))}
									</Select>

									<Button
										color="success"
										size="sm"
										className="w-full sm:w-auto"
										startContent={<Plus className="w-4 h-4" />}
										onPress={handleAddUser}
										isLoading={addingUser}
										isDisabled={!selectedUserId || !selectedUserAccessibility}
									>
										Add
									</Button>
								</div>
							</div>

							{/* Permissions Table */}
							{/* `removeWrapper` deletes HeroUI's own overflow slot, and the
							    fixed columns floor the table at ~460px — fence it so a phone
							    scrolls the TABLE, not the whole modal body. */}
							<div className="overflow-x-auto">
							<Table
								aria-label="Resource permissions"
								removeWrapper
								classNames={{
									// px-2 below sm: the default px-3 spends ~96px on padding
									// across four columns — the difference between the delete
									// icon fitting a 390px phone and a sideways scroll.
									th: 'bg-default-100 px-2 sm:px-3',
									td: 'px-2 sm:px-3'
								}}
							>
								<TableHeader>
									<TableColumn key="type" className="hidden sm:table-cell">TYPE</TableColumn>
									<TableColumn key="name">NAME</TableColumn>
									<TableColumn key="accessibility">ACCESSIBILITY</TableColumn>
									<TableColumn key="actions" width={32}></TableColumn>
								</TableHeader>
								<TableBody>
									{/* Resource row - highlighted */}
									<TableRow key="resource" className="bg-warning-50 dark:bg-warning-900/20">
										<TableCell className="hidden sm:table-cell">
											<span className="font-semibold">{getResourceTypeName()}</span>
										</TableCell>
										<TableCell>
											<span className="font-medium">{resource?.name}</span>
											{/* On phone the hidden TYPE column's word rides under the name. */}
											<span className="sm:hidden block text-[11px] text-default-400">
												{getResourceTypeName()}
											</span>
										</TableCell>
										<TableCell>
											<Select
												aria-label="Resource accessibility"
												selectedKeys={resourceAccessibility ? [resourceAccessibility] : []}
												onSelectionChange={handleResourceAccessibilityChange}
												size="sm"
												isDisabled={saving}
												className="min-w-[140px]"
												classNames={{ trigger: 'min-h-8 h-8' }}
											>
												{accessibilityTypes.map(status => (
													<SelectItem key={status.value}>
														{formatAccessibilityName(status.name)}
													</SelectItem>
												))}
											</Select>
										</TableCell>
										<TableCell></TableCell>
									</TableRow>

									{/* User permissions rows */}
									{enrichedUserPermissions.length === 0 ? (
										<TableRow key="empty">
											<TableCell colSpan={4}>
												<div className="text-center py-4 text-warning-600">
													You don't have any additional rules for users
												</div>
											</TableCell>
										</TableRow>
									) : (
										enrichedUserPermissions.map(user => {
											const userId = user.userID || user.userId || user.id;
											const inactive = isUserInactive(user);
											return (
												<TableRow key={userId} className={inactive ? 'opacity-60' : ''}>
													<TableCell className="hidden sm:table-cell"></TableCell>
													<TableCell>
														<div className="flex items-center gap-2">
															{getUserDisplayName(user)}
															{inactive && (
																<Chip size="sm" color="default" variant="flat">
																	Inactive
																</Chip>
															)}
														</div>
													</TableCell>
													<TableCell>
														<Select
															aria-label="User accessibility"
															selectedKeys={user.accessibility ? [user.accessibility] : []}
															onSelectionChange={keys =>
																handleUserAccessibilityChange(userId, keys)
															}
															size="sm"
															className="min-w-[140px]"
												classNames={{ trigger: 'min-h-8 h-8' }}
														>
															{accessibilityTypes.map(status => (
																<SelectItem key={status.value}>
																	{formatAccessibilityName(status.name)}
																</SelectItem>
															))}
														</Select>
													</TableCell>
													<TableCell className="px-0">
														<Button
															isIconOnly
															size="sm"
															variant="light"
															color="danger"
															onPress={() => handleDeleteUser(userId)}
															aria-label="Remove user permission"
														>
															<Trash2 className="w-4 h-4" />
														</Button>
													</TableCell>
												</TableRow>
											);
										})
									)}
								</TableBody>
							</Table>
							</div>

						</div>
					)}
				</ModalBody>
				<ModalFooter>
					<Button variant="light" onPress={onClose}>
						Close
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

ResourcePermissionsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	resource: PropTypes.object,
	users: PropTypes.array
};

export default ResourcePermissionsModal;
